import Head from 'next/head';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next/pages';
import localeConfig from '/assets/localization/config.json';
import uniq from 'lodash/uniq';
import rewrites from 'urlTranslations';
import { alternatePath, filledPathname } from 'utils/routePath';
import { siteOrigin } from 'utils/siteOrigin';

interface CustomHeadProps {
  titleProduction?: string;
  titleStaging?: string;
  metaDescriptionProd?: string;
  metaDescriptionStaging?: string;
  canonicalProd?: string;
  canonicalStaging?: string;
}

const CustomHead: React.FC<CustomHeadProps> = ({
  titleProduction,
  titleStaging,
  metaDescriptionProd,
  metaDescriptionStaging,
  canonicalProd,
  canonicalStaging,
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  // The English address, with a variable route part filled in.
  const enPathname = filledPathname(router.pathname, router.query);
  const isProd = process.env.NEXT_PUBLIC_ENV === 'production';

  // Determine the title
  const title = isProd
    ? titleProduction
      ? t(titleProduction)
      : ''
    : titleStaging;

  // WHIT-524: a page that names no canonical address still gets the domain.
  const origin = siteOrigin(isProd, canonicalProd, canonicalStaging);

  const renderHrefLangsWithoutRegion = () => {
    const baseLocales = localeConfig.locales.map(loc =>
      (process.env.NEXT_PUBLIC_I18N_SUPPORTED_LANGS as any).includes(loc.locale)
        ? loc.localeWithRegion.split('-')?.[0]
        : null,
    );
    const uniqueBaseLocales = uniq(baseLocales);
    return uniqueBaseLocales.map(lan => {
      const pathname = lan
        ? alternatePath(lan, enPathname, router.asPath, rewrites)
        : '';
      return (
        lan && (
          <link
            key={lan}
            rel="alternate"
            hrefLang={lan}
            href={`${origin}${pathname}`}
          />
        )
      );
    });
  };

  const renderHrefLangsWithRegion = () => {
    if (!localeConfig.locales || !process.env.NEXT_PUBLIC_I18N_SUPPORTED_LANGS)
      return null;

    return localeConfig.locales.map(loc => {
      if (
        (process.env.NEXT_PUBLIC_I18N_SUPPORTED_LANGS as any).includes(
          loc.locale,
        )
      ) {
        // Excluding locales which have excludeFromHrefLang field in the config.
        // Locales such as de-DE, hu-HU (where the region and the base locale is the same), should be excluded,
        // because we are already rendering a hrefling with the base locale with the renderHrefLangsWithoutRegion()
        if (!loc.excludeFromHrefLang) {
          const pathname = alternatePath(
            loc.locale,
            enPathname,
            router.asPath,
            rewrites,
          );

          return (
            <link
              key={loc.localeWithRegion}
              rel="alternate"
              hrefLang={loc.localeWithRegion}
              href={`${origin}${pathname}`}
            />
          );
        }
      }
      return null;
    });
  };

  return (
    <Head>
      <title>{title}</title>
      <meta
        name="description"
        content={
          isProd
            ? metaDescriptionProd
              ? t(`${metaDescriptionProd}`) || ''
              : ''
            : metaDescriptionStaging
              ? metaDescriptionStaging
              : ''
        }
      />
      <meta property="og:title" content={title} />
      <meta property="og:type" content="website" />
      <meta
        property="og:url"
        content={isProd ? canonicalProd : canonicalStaging}
      />
      <meta
        property="og:description"
        content={
          isProd
            ? (metaDescriptionProd ? t(metaDescriptionProd) : '') || ''
            : metaDescriptionStaging
        }
      />
      <meta
        property="og:image"
        content={`https://allmyles-airline-logos.s3.eu-central-1.amazonaws.com/Page_logos/${
          isProd ? 'TipptourLogoLarge.jpg' : 'ShowcaseLogoLarge.jpg'
        }`}
      />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta
        property="og:image"
        content={`https://allmyles-airline-logos.s3.eu-central-1.amazonaws.com/Page_logos/${
          isProd ? 'TipptourLogoMedium.jpg' : 'ShowcaseLogoMedium.jpg'
        }`}
      />
      <meta property="og:image:width" content="600" />
      <meta property="og:image:height" content="315" />
      <meta
        property="og:image"
        content={`https://allmyles-airline-logos.s3.eu-central-1.amazonaws.com/Page_logos/${
          isProd ? 'TipptourLogoSmall.png' : 'AllmylesLogoSmall.png'
        }`}
      />
      <meta property="og:image:width" content="300" />
      <meta property="og:image:height" content="157" />
      <meta
        property="og:site_name"
        content={
          isProd ? t('fb_graph_site_name_prod') || 'Tipptour' : 'Showcase'
        }
      />
      <meta name="twitter:card" content="summary_large_image" />
      <meta
        name="twitter:image"
        content={`https://allmyles-airline-logos.s3.eu-central-1.amazonaws.com/Page_logos/${
          isProd ? 'TipptourLogo.jpg' : 'ShowcaseLogo.jpg'
        }`}
      />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:domain" content={origin.replace('https://', '')} />
      <meta
        name="twitter:url"
        content={isProd ? canonicalProd : canonicalStaging}
      />
      <meta
        name="twitter:description"
        content={
          isProd
            ? (metaDescriptionProd ? t(metaDescriptionProd) : '') || ''
            : metaDescriptionStaging
        }
      />
      <link
        rel="canonical"
        key="canonical"
        href={`${origin}/${router.locale}${router.asPath}`}
      />
      {
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`${origin}/en${enPathname === '/' ? '' : enPathname}/`}
        />
      }
      {renderHrefLangsWithoutRegion()}
      {renderHrefLangsWithRegion()}
    </Head>
  );
};

export default CustomHead;
